import { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal } from "./Reveal";

const BEFORE = "https://static.prod-images.emergentagent.com/jobs/39cf9e58-4462-4aa0-960e-9ae9951fc58d/images/db3d754a16cfbb0f70fb88bab41609593a42ebf4451cec2417dc18b67ede09bc.jpeg";
const AFTER = "https://static.prod-images.emergentagent.com/jobs/39cf9e58-4462-4aa0-960e-9ae9951fc58d/images/dd6bd01c6cce64712e15520af3ec4bbe7734a156284000760f8b4ec9ac6f62cf.jpeg";

export const BeforeAfter = () => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      update(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [update]);

  return (
    <section id="transformations" data-testid="before-after" className="bg-pine text-bone py-24 md:py-32 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[1px] w-10 bg-terra" />
              <span className="text-bone/60 text-xs uppercase tracking-[0.3em]">The transformation</span>
            </div>
            <Reveal>
              <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">
                Drag to see the difference.
              </h2>
            </Reveal>
          </div>
          <p className="text-bone/70 text-base md:text-lg max-w-sm leading-relaxed">
            A hazardous, half-dead oak leaning on the house — removed, cleared, and the yard
            left cleaner than we found it.
          </p>
        </div>

        <Reveal>
          <div
            ref={wrapRef}
            data-testid="ba-slider"
            className="relative w-full aspect-[3/2] md:aspect-[16/9] overflow-hidden border border-white/10 select-none cursor-ew-resize"
            onMouseDown={(e) => { dragging.current = true; update(e.clientX); }}
            onTouchStart={(e) => { dragging.current = true; update(e.touches[0].clientX); }}
          >
            {/* After (base) */}
            <img src={AFTER} alt="After tree removal" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
            <span className="absolute top-4 right-4 z-10 bg-terra text-bone text-xs font-bold uppercase tracking-widest px-3 py-1.5">After</span>

            {/* Before (clipped via clip-path, stays full-size) */}
            <img
              src={BEFORE}
              alt="Before tree removal"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
              draggable={false}
            />
            <span
              className="absolute top-4 left-4 z-10 bg-pine/90 text-bone text-xs font-bold uppercase tracking-widest px-3 py-1.5 border border-white/20"
              style={{ opacity: pos > 12 ? 1 : 0 }}
            >
              Before
            </span>

            {/* Handle */}
            <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
              <div className="w-[2px] h-full bg-bone" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-bone text-pine flex items-center justify-center shadow-lg">
                <MoveHorizontal size={20} />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
